import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database, Json } from "@/integrations/supabase/types";
import type { TranscriptionRowLike } from "./transcription-status";

/**
 * Intersecao com `TranscriptionRowLike` para estreitar `ai_status` de `string`
 * (como vem do schema gerado) para o union `AiStatus`, usado na maquina de estados.
 */
export type ConsultationTranscriptionRow = Database["public"]["Tables"]["consultation_transcriptions"]["Row"] &
    TranscriptionRowLike;

interface UseTranscriptionSessionParams {
    initialTranscriptionId?: string | null;
    petId: string;
    clinicId: string | null;
    appointmentId: string | null;
    medicalRecordId: string | null;
    metadata: Record<string, unknown>;
}

export const useTranscriptionSession = ({
    initialTranscriptionId,
    petId,
    clinicId,
    appointmentId,
    medicalRecordId,
    metadata,
}: UseTranscriptionSessionParams) => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [isSessionLoading, setIsSessionLoading] = useState(false);
    const [sessionError, setSessionError] = useState<string | null>(null);
    const [history, setHistory] = useState<ConsultationTranscriptionRow[]>([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    const sessionIdRef = useRef<string | null>(null);
    const hasLoadedHistoryRef = useRef(false);

    /**
     * Fetches transcription history for the current pet
     */
    const fetchHistory = useCallback(async () => {
        if (!petId) return;
        try {
            setIsHistoryLoading(true);
            console.log("Fetching history for pet:", petId);

            const { data, error: historyError } = await supabase
                .from("consultation_transcriptions")
                .select("*")
                .eq("pet_id", petId)
                .eq("status", "finalized")
                .order("created_at", { ascending: false })
                .limit(5);

            if (historyError) {
                console.error("History fetch error:", historyError);
                throw historyError;
            }

            console.log("History fetched:", {
                count: data?.length ?? 0,
                records: data?.map(d => ({
                    id: d.id,
                    status: d.status,
                    hasTranscript: !!d.transcript,
                    hasSuggestions: Array.isArray(d.diagnosis_suggestions) && d.diagnosis_suggestions.length > 0,
                    created_at: d.created_at
                }))
            });

            setHistory((data ?? []) as ConsultationTranscriptionRow[]);
        } catch (historyError) {
            console.error("Erro ao carregar histórico de transcrições:", historyError);
        } finally {
            setIsHistoryLoading(false);
        }
    }, [petId]);

    /**
     * Loads the current recoverable session for the pet
     */
    const fetchSessionById = useCallback(async (
        id: string,
        options?: { persistSessionId?: boolean },
    ): Promise<ConsultationTranscriptionRow | null> => {
        try {
            setIsSessionLoading(true);
            const { data, error } = await supabase
                .from("consultation_transcriptions")
                .select("*")
                .eq("id", id)
                .maybeSingle();

            if (error) throw error;

            const typedData = data as ConsultationTranscriptionRow | null;

            if (typedData && options?.persistSessionId !== false) {
                sessionIdRef.current = typedData.id;
                setSessionId(typedData.id);
            }

            return typedData;
        } catch (loadSessionError) {
            console.error("Erro ao carregar sessão por id:", loadSessionError);
            return null;
        } finally {
            setIsSessionLoading(false);
        }
    }, []);

    const loadLatestSession = useCallback(async (options?: {
        blockedPetId?: string | null;
        blockedSessionId?: string | null;
    }): Promise<ConsultationTranscriptionRow | null> => {
        if (options?.blockedPetId && petId && options.blockedPetId === petId) {
            return null;
        }

        if (initialTranscriptionId) {
            const existing = await fetchSessionById(initialTranscriptionId, { persistSessionId: false });
            if (existing) {
                if (options?.blockedSessionId && existing.id === options.blockedSessionId) {
                    return null;
                }

                sessionIdRef.current = existing.id;
                setSessionId(existing.id);
                return existing;
            }
        }

        if (!petId) return null;

        try {
            setIsSessionLoading(true);
            const { data, error: loadError } = await supabase
                .from("consultation_transcriptions")
                .select("*")
                .eq("pet_id", petId)
                .eq("status", "draft")
                .order("created_at", { ascending: false })
                .limit(1)
                .maybeSingle();

            if (loadError) throw loadError;

            const typedData = data as ConsultationTranscriptionRow | null;

            if (typedData && options?.blockedSessionId && typedData.id === options.blockedSessionId) {
                return null;
            }

            if (typedData) {
                sessionIdRef.current = typedData.id;
                setSessionId(typedData.id);
            }

            return typedData;
        } catch (loadSessionError) {
            console.error("Erro ao carregar sessão:", loadSessionError);
            return null;
        } finally {
            setIsSessionLoading(false);
        }
    }, [fetchSessionById, initialTranscriptionId, petId]);

    /**
     * Creates or retrieves the current transcription session
     */
    const ensureSession = useCallback(async (): Promise<string | null> => {
        if (sessionIdRef.current) {
            return sessionIdRef.current;
        }

        if (!clinicId) {
            setSessionError("Configure a clínica atual para usar a transcrição.");
            return null;
        }

        if (!petId) {
            setSessionError("Selecione um paciente para iniciar a transcrição.");
            return null;
        }

        try {
            setIsSessionLoading(true);
            setSessionError(null);

            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user) {
                throw new Error("Usuário não autenticado.");
            }

            // Don't include medical_record_id in insert as it doesn't exist yet
            // It will be linked when the medical record is saved
            const { data, error: insertError } = await supabase
                .from("consultation_transcriptions")
                .insert({
                    pet_id: petId,
                    clinic_id: clinicId,
                    appointment_id: appointmentId ?? null,
                    vet_user_id: userData.user.id,
                    status: "draft",
                    metadata: metadata as Json,
                })
                .select()
                .single();

            if (insertError) throw insertError;

            sessionIdRef.current = data.id;
            setSessionId(data.id);
            return data.id;
        } catch (sessionCreationError) {
            console.error("Erro ao iniciar sessão de transcrição:", sessionCreationError);
            setSessionError(
                sessionCreationError instanceof Error
                    ? sessionCreationError.message
                    : "Não foi possível iniciar a sessão de transcrição.",
            );
            return null;
        } finally {
            setIsSessionLoading(false);
        }
    }, [appointmentId, clinicId, metadata, petId]);

    /**
     * Resets session state
     */
    const resetSession = useCallback(() => {
        setSessionId(null);
        sessionIdRef.current = null;
        setSessionError(null);
        setHistory([]);
        setIsHistoryLoading(false);
        hasLoadedHistoryRef.current = false;
    }, []);

    const updateHistoryEntry = useCallback(
        (id: string, changes: Partial<ConsultationTranscriptionRow>) => {
            setHistory((previous) =>
                previous.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)),
            );
        },
        [],
    );

    return {
        sessionId,
        sessionIdRef,
        isSessionLoading,
        sessionError,
        history,
        isHistoryLoading,
        hasLoadedHistoryRef,
        fetchHistory,
        fetchSessionById,
        loadLatestSession,
        ensureSession,
        resetSession,
        updateHistoryEntry,
    };
};
